import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, User, Menu, X, LayoutDashboard, LogOut, History, Gift, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import CartSheet from './CartSheet';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import { apiRequest } from '../lib/utils';

const Navbar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { cartCount, isOpen, setIsOpen } = useCart();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [hasActivePromos, setHasActivePromos] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    apiRequest('/promotions/all')
      .then(data => {
        const total = (data?.promotions?.length || 0) + (data?.promo_codes?.length || 0);
        setHasActivePromos(total > 0);
      })
      .catch(() => {});
  }, []);

  // Check if we're in admin section - use English for admin
  const isAdminPage = location.pathname.startsWith('/admin');
  
  const navLinks = isAdminPage ? [
    { href: '/', label: 'Home' },
    { href: '/menu', label: 'Menu' },
    { href: '/contact', label: 'Contact' },
  ] : [
    { href: '/', label: t('nav.home') },
    { href: '/menu', label: t('nav.menu') },
    { href: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`sticky top-0 z-[101] w-full transition-all duration-300 backdrop-blur-xl ${scrolled ? 'bg-[#1C1C22]/95 shadow-lg shadow-black/30' : 'bg-[#1C1C22]/85'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 lg:h-[72px] items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
            <span className="hidden lg:block"><Logo size="header" /></span>
            <span className="lg:hidden"><Logo size="small" /></span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
                className={`text-base font-medium transition-colors hover:text-[#FFC107] ${
                  isActive(link.href) ? 'text-[#FFC107]' : 'text-zinc-300'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-1 sm:gap-4">
            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Promotions Button */}
            {hasActivePromos && (
              <Button
                variant="ghost"
                size="icon"
                className="relative h-9 w-9"
                data-testid="promo-nav-button"
                onClick={() => window.dispatchEvent(new Event('open-promo-modal'))}
              >
                <Tag className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 animate-pulse" />
              </Button>
            )}

            {/* Cart Button */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-9 w-9"
                  data-testid="cart-button"
                >
                  <ShoppingCart className="h-5 w-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-[#FFC107] text-black text-xs font-bold flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg bg-[#0B0B0C] border-[#27272A]">
                <CartSheet />
              </SheetContent>
            </Sheet>

            {/* User Menu - desktop only, mobile uses hamburger menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="hidden lg:flex" data-testid="user-menu-button">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#141417] border-[#27272A] z-[200]">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-zinc-400">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-[#1C1C22]" />
                  <DropdownMenuItem asChild>
                    <Link to="/orders" className="flex items-center gap-2 cursor-pointer" data-testid="my-orders-link">
                      <History className="h-4 w-4" />
                      {isAdminPage ? 'My Orders' : t('nav.myOrders')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/loyalty" className="flex items-center gap-2 cursor-pointer" data-testid="loyalty-card-link">
                      <Gift className="h-4 w-4 text-[#FFC107]" />
                      {isAdminPage ? 'Stamp Card' : t('nav.stampCard')}
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 cursor-pointer" data-testid="admin-link">
                        <LayoutDashboard className="h-4 w-4" />
                        {isAdminPage ? 'Admin' : t('nav.admin')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-[#1C1C22]" />
                  <DropdownMenuItem onClick={logout} className="flex items-center gap-2 cursor-pointer text-red-400" data-testid="logout-button">
                    <LogOut className="h-4 w-4" />
                    {isAdminPage ? 'Logout' : t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="hidden lg:block">
                <Button variant="outline" size="sm" className="border-zinc-700 hover:bg-[#25252E]" data-testid="login-button">
                  <span>{isAdminPage ? 'Login' : t('nav.login')}</span>
                </Button>
              </Link>
            )}

            {/* Mobile menu button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 bg-[#0B0B0C] border-[#27272A]">
                <div className="flex flex-col gap-4 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-lg font-medium py-2 ${
                        isActive(link.href) ? 'text-[#FFC107]' : 'text-zinc-300'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="border-t border-[#27272A] pt-4 mt-2">
                    {hasActivePromos && (
                      <button onClick={() => { setMobileMenuOpen(false); window.dispatchEvent(new Event('open-promo-modal')); }} className="flex items-center gap-2 text-[#FFC107] py-2 font-medium">
                        <Tag className="h-4 w-4" /> {isAdminPage ? 'Promotions' : t('nav.promotions')}
                        <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                      </button>
                    )}
                    {isAuthenticated ? (
                      <>
                        <p className="text-sm text-zinc-400 mb-3">{user?.name}</p>
                        <Link to="/orders" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 text-zinc-300 py-2">
                          <History className="h-4 w-4" /> {isAdminPage ? 'My Orders' : t('nav.myOrders')}
                        </Link>
                        <Link to="/loyalty" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 text-zinc-300 py-2">
                          <Gift className="h-4 w-4 text-[#FFC107]" /> {isAdminPage ? 'Stamp Card' : t('nav.stampCard')}
                        </Link>
                        {isAdmin && (
                          <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 text-zinc-300 py-2">
                            <LayoutDashboard className="h-4 w-4" /> {isAdminPage ? 'Admin' : t('nav.admin')}
                          </Link>
                        )}
                        <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="flex items-center gap-2 text-red-400 py-2 mt-2">
                          <LogOut className="h-4 w-4" /> {isAdminPage ? 'Logout' : t('nav.logout')}
                        </button>
                      </>
                    ) : (
                      <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 text-[#FFC107] font-medium py-2" data-testid="mobile-login-link">
                        <User className="h-4 w-4" /> {isAdminPage ? 'Login' : t('nav.login')}
                      </Link>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#FFC107]/70 to-transparent" aria-hidden="true" />
    </header>
  );
};

export default Navbar;
